import { Component } from "@angular/core";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { MACHINE_TIME_LEGEND, MACHINE_TIME_TICKS, MachineTimeBars, MachineTimeClock, MachineTimeSummary } from "@helpers/machineTime";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "employee-machine-time",
	styleUrls: ["employeeMachineTime.css"],
	templateUrl: "employeeMachineTime.html"
})
export class EmployeeMachineTimeAdminComponent {
	date: string | number | Date = new Date();
	// Everyone the day returned, for the picker. Rebuilt on every load because who was in changes by the day.
	employees: { id: number, name: string }[] = [];
	headerPrimaryButtons: AdminHeaderButton[] = [];
	isLoading = false;
	// Mirrors the roles on UserProfileController.MachineTimeForDay. The endpoint is the real gate - this only
	// keeps the page from loading figures it would be refused anyway.
	isSuperAdmin = false;
	legend = MACHINE_TIME_LEGEND;
	rows: any[] = [];
	// 0 is the All tab; -1 is the tab for anyone the day cannot credit to a site.
	selectedTabId = 0;
	// The strip is driven by index, so it is kept in step with the id by hand. Without it, changing the date
	// would redraw the strip on All while the bands were still filtered to the site that was open.
	selectedTabIndex = 0;
	selectedUserProfileIds: number[] = [];
	tabs: any[] = [];
	ticks = MACHINE_TIME_TICKS;
	tip: any = null;
	title = "Machine Time";
	today = new Date();
	visibleRows: any[] = [];

	constructor(private toastService: ToastService, private itemService: UserProfileService, appSettingsService: AppSettingsService) {
		this.isSuperAdmin = CheckGroupPermissions(appSettingsService, "Super Admin");
		this.buildButtons();
		this.getData();
	}

	applyFilter() {
		this.visibleRows = this.rows.filter((row) =>
			this.selectedUserProfileIds.indexOf(row.userProfileId) > -1
			&& (this.selectedTabId === 0 || (this.selectedTabId === -1 ? !row.siteId : row.siteId === this.selectedTabId))
		);
	}

	buildButtons() {
		this.headerPrimaryButtons = [{ icon: "fal fa-file-pdf", method: "downloadPdf", text: "Download PDF" }];
	}

	// A tab per site the day actually touched, rather than every site on the system, so empty tabs do not appear.
	// Grouped on the site id the row carries, because the name is blank for someone with no site at all.
	buildTabs() {
		const sites: any[] = [];
		for (const row of this.rows) {
			if (!row.siteId) continue;
			if (sites.some((site) => site.id === row.siteId)) continue;
			sites.push({ id: row.siteId, text: row.siteName || "Site " + row.siteId });
		}

		sites.sort((a, b) => a.text.localeCompare(b.text));
		this.tabs = [{ id: 0, text: "All" }, ...sites];

		if (this.rows.some((row) => !row.siteId)) {
			this.tabs.push({ id: -1, text: "No site" });
		}

		// Keep the site that was open if the new day still has it, and fall back to All if it does not.
		const open = this.tabs.findIndex((tab) => tab.id === this.selectedTabId);
		this.selectedTabId = open === -1 ? 0 : this.selectedTabId;
		this.selectedTabIndex = open === -1 ? 0 : open;
	}

	changeDate(e: any) {
		this.date = e.value;
		this.getData();
	}

	// The whole day as the nightly Employee Stat Report prints it - everyone, not just who is left selected on
	// screen, so a downloaded copy and the emailed one are the same document.
	downloadPdf() {
		if (this.isSuperAdmin === false) return;
		const date = new Date(this.date || new Date());
		this.itemService.machineTimeForDayPdf(date)
			.subscribe(
				(res: any) => {
					const link = document.createElement("a");
					link.href = window.URL.createObjectURL(new Blob([res], { type: "application/pdf" }));
					link.setAttribute("download", `MachineTime_${date.toISOString().slice(0, 10)}.pdf`);
					document.body.appendChild(link);
					link.click();
					link.remove();
				},
				(err) => { console.log(err); this.toastService.error("Could not build the PDF."); }
			);
	}

	getData() {
		if (this.isSuperAdmin === false) return;
		this.isLoading = true;
		this.itemService.machineTimeForDay(new Date(this.date || new Date()))
			.subscribe(
				(res: any) => {
					// Site then name, the order the API returns them and the report prints them.
					this.rows = (res.response || [])
						.map((row: any) => ({ ...row, bars: MachineTimeBars(row), clock: MachineTimeClock(row), summary: MachineTimeSummary(row) }));
					this.employees = this.rows.map((row: any) => ({ id: row.userProfileId, name: row.name }));
					// Everyone to begin with, so the day reads whole before it is narrowed down.
					this.selectedUserProfileIds = this.employees.map((employee) => employee.id);
					this.buildTabs();
					this.applyFilter();
					this.isLoading = false;
				},
				(err) => { console.log(err); this.toastService.error("Could not load machine time."); this.isLoading = false; }
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore
		if (this[method]) this[method]();
	}

	hideTip() {
		this.tip = null;
	}

	// Dropping someone from the band list, the other way round from the picker above it.
	removeEmployee(userProfileId: number) {
		this.selectedUserProfileIds = this.selectedUserProfileIds.filter((id) => id !== userProfileId);
		this.applyFilter();
	}

	selectAll() {
		this.selectedUserProfileIds = this.employees.map((employee) => employee.id);
		this.applyFilter();
	}

	selectNone() {
		this.selectedUserProfileIds = [];
		this.applyFilter();
	}

	selectTab(e: any) {
		this.selectedTabId = this.tabs[e.itemIndex].id;
		this.selectedTabIndex = e.itemIndex;
		this.applyFilter();
	}

	// A hover card on the band, the same one the machine time tab on a profile uses.
	showTip(bar: any, event: MouseEvent) {
		const box = (event.target as HTMLElement).getBoundingClientRect();
		this.tip = {
			...bar,
			x: Math.max(8, Math.min(window.innerWidth - 240, box.left + box.width / 2 - 110)),
			y: box.top - 8
		};
	}

	viewToday() {
		this.date = new Date();
		this.getData();
	}
}
